import { BULLET_LIST, ORDERED_LIST } from "constants/richText";
import { Lexer, Token, Tokens } from "marked";
import Delta, { AttributeMap } from "quill-delta";

const NEWLINE = "\n";

const TOKEN_TYPE = {
  STRONG: "strong",
  EM: "em",
  LINK: "link",
  BR: "br",
  LIST: "list",
  SPACE: "space",
} as const;

const isList = (token: Token): token is Tokens.List =>
  token.type === TOKEN_TYPE.LIST;

const childTokens = (token: Token): Token[] =>
  "tokens" in token && token.tokens ? token.tokens : [token];

const insertText = (delta: Delta, text: string, attributes: AttributeMap) => {
  const hasAttributes = Object.keys(attributes).length > 0;

  text.split(NEWLINE).forEach((part, index) => {
    if (index > 0) delta.insert(NEWLINE);
    if (part) delta.insert(part, hasAttributes ? attributes : undefined);
  });
};

const appendInline = (
  delta: Delta,
  tokens: Token[],
  attributes: AttributeMap
) => {
  tokens.forEach((token) => {
    if (token.type === TOKEN_TYPE.STRONG) {
      appendInline(delta, token.tokens ?? [], { ...attributes, bold: true });
      return;
    }

    if (token.type === TOKEN_TYPE.EM) {
      appendInline(delta, token.tokens ?? [], { ...attributes, italic: true });
      return;
    }

    if (token.type === TOKEN_TYPE.LINK) {
      appendInline(delta, token.tokens ?? [], {
        ...attributes,
        link: token.href,
      });
      return;
    }

    if (token.type === TOKEN_TYPE.BR) {
      delta.insert(NEWLINE);
      return;
    }

    if ("tokens" in token && token.tokens && token.tokens.length > 0) {
      appendInline(delta, token.tokens, attributes);
      return;
    }

    const text = "text" in token ? String(token.text) : token.raw;
    insertText(delta, text, attributes);
  });
};

const appendList = (delta: Delta, list: Tokens.List) => {
  const lineFormat = { list: list.ordered ? ORDERED_LIST : BULLET_LIST };

  list.items.forEach((item) => {
    let isLineOpen = true;

    item.tokens.forEach((token) => {
      if (isList(token)) {
        if (isLineOpen) delta.insert(NEWLINE, lineFormat);
        isLineOpen = false;
        appendList(delta, token);
        return;
      }

      appendInline(delta, childTokens(token), {});
    });

    if (isLineOpen) delta.insert(NEWLINE, lineFormat);
  });
};

const countNewlines = (text: string) => text.split(NEWLINE).length - 1;

export const markdownToDelta = (markdown: string): Delta => {
  const delta = new Delta();
  let isAfterList = false;

  Lexer.lex(markdown, { gfm: true }).forEach((token) => {
    if (token.type === TOKEN_TYPE.SPACE) {
      // A list needs a blank line before the next paragraph, so that one isn't content
      const blankLines = countNewlines(token.raw) - (isAfterList ? 2 : 1);
      if (blankLines > 0) delta.insert(NEWLINE.repeat(blankLines));
      return;
    }

    isAfterList = isList(token);

    if (isList(token)) {
      appendList(delta, token);
      return;
    }

    appendInline(delta, childTokens(token), {});
    delta.insert(NEWLINE);
  });

  return delta;
};
